import { OnEvent, Service } from '@toeverything/infra';

import type { AuthAccountInfo } from '../../cloud/entities/session';
import { AccountLoggedOut } from '../../cloud/events/account-logged-out';
import type { ServerService } from '../../cloud/services/server';
import {
  defaultWorkspaceApiClient,
  WorkspaceApiError,
} from '../../cloud/services/workspace-api-client';
import type { WorkspaceServerService } from '../../cloud/services/workspace-server';
import {
  type AppReleaseCheckResult,
  checkAppReleaseConfirmation,
} from '../../local-authority/app-release-check';
import {
  type AppReleaseEnvelope,
  AppReleaseRequestError,
  type AppReleaseScope,
  AppReleaseStore,
} from '../../local-authority/app-release-store';
import type { PermissionSnapshotStore } from '../stores/permission-snapshot';

/** No confirmation could be fetched and none is stored for this person. */
export type AppReleaseConfirmationState = AppReleaseCheckResult | 'unconfirmed';

export interface AppReleaseConfirmation {
  readonly state: AppReleaseConfirmationState;
  /** The checked confirmation; null when none was fetched or stored. */
  readonly envelope: AppReleaseEnvelope | null;
}

function createAppReleaseStore(): AppReleaseStore {
  return new AppReleaseStore(requestAppRelease);
}

async function requestAppRelease(
  scope: AppReleaseScope
): Promise<AppReleaseEnvelope> {
  try {
    return await defaultWorkspaceApiClient.requestJson<AppReleaseEnvelope>({
      workspaceId: scope.workspaceId,
      baseUrl: scope.serverBaseUrl,
      path: new URL(
        `/api/workspaces/${encodeURIComponent(scope.workspaceId)}/app-bases/${encodeURIComponent(scope.appBaseId)}/release-snapshot`,
        scope.serverBaseUrl
      ).href,
      category: 'permissions',
      mode: 'foreground',
      init: { method: 'POST' },
    });
  } catch (error) {
    throw new AppReleaseRequestError(
      error instanceof WorkspaceApiError ? error.status : null
    );
  }
}

/**
 * Confirms the release of a published App before a member's embedding runs
 * it: the signed confirmation of this person (fetched once per App start,
 * kept in the permission vault for offline use) checked against the
 * manifest that is about to run.
 */
export class AppReleaseConfirmationService extends Service {
  private readonly releases = createAppReleaseStore();

  constructor(
    private readonly permissionSnapshotStore: PermissionSnapshotStore,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  async confirm(
    appBaseId: string,
    manifest: unknown
  ): Promise<AppReleaseConfirmation> {
    const server = this.workspaceServerService.server;
    const scope = this.permissionSnapshotStore.currentScope();
    if (!server || !scope) return { state: 'unconfirmed', envelope: null };
    let envelope: AppReleaseEnvelope;
    try {
      envelope = await this.releases.fetchOnStart({
        serverBaseUrl: server.baseUrl,
        userId: scope.userId,
        workspaceId: scope.workspaceId,
        appBaseId,
      });
    } catch (error) {
      if (error instanceof AppReleaseRequestError) {
        return { state: 'unconfirmed', envelope: null };
      }
      throw error;
    }
    const state = await checkAppReleaseConfirmation(
      envelope,
      { ...scope, appBaseId },
      manifest,
      new Date()
    );
    return { state, envelope };
  }
}

/** Signing out drops every App release confirmation of that person. */
@OnEvent(AccountLoggedOut, service => service.onAccountLoggedOut)
export class AppReleaseSignOutService extends Service {
  private readonly releases = createAppReleaseStore();

  constructor(private readonly serverService: ServerService) {
    super();
  }

  onAccountLoggedOut(account: AuthAccountInfo): Promise<void> {
    return this.releases.clear({
      serverBaseUrl: this.serverService.server.baseUrl,
      userId: account.id,
    });
  }
}
